import { CurrentHealthcareProviderPaymentsConfigurationQuery } from '@dreryk/egabinet-online-payments-integration-frontend-api';

import { Header, MarginPosition, Message, MessageType } from '@dreryk/react-components';
import { useCqrsExists } from '@dreryk/command-query-react-common';

import { protectFeature } from '~libs.shared.system';

import { PageLoader } from '../shared/components/PageLoader';
import { useCqrsSearchPractitionerProvidingPrivateMedicalService } from '../shared/queries/useCqrsSearchPractitionerProvidingPrivateMedicalServiceQuery';
import { entryTranslationKeys, medicalServiceTranslationKeys } from '../translations';

import { PrivateMedicalServiceNavTabs } from './PrivateMedicalServiceNavTabs';

type MedicalServiceTableLoaderRenderProps = {
   hasMorePractitioners: boolean;
   onlinePaymentsIntegrationExists: boolean;
};

type MedicalServiceTableLoaderProps = {
   readonly children: (renderProps: MedicalServiceTableLoaderRenderProps) => JSX.Element;
};

const PrivateMedicalServicePanelLoader = (props: MedicalServiceTableLoaderProps): JSX.Element | null => {
   const managingOnlinePaymentsConfigurationAvailable = protectFeature('managingOnlinePaymentsConfiguration');

   const {
      data: onlinePaymentsIntegrationConfiguration,
      loading: onlinePaymentsIntegrationConfigurationLoading,
      error: onlinePaymentsIntegrationConfigurationError,
   } = useCqrsExists(() => new CurrentHealthcareProviderPaymentsConfigurationQuery(), {
      enabled: managingOnlinePaymentsConfigurationAvailable,
   });

   const {
      data: practitioners,
      loading: practitionersLoading,
      error: practitionersError,
   } = useCqrsSearchPractitionerProvidingPrivateMedicalService();

   if (practitionersLoading || onlinePaymentsIntegrationConfigurationLoading) {
      return <PageLoader />;
   }

   if (practitionersError || onlinePaymentsIntegrationConfigurationError) {
      return (
         <Message
            testId={'medicalServiceTableLoaderError'}
            type={MessageType.NEGATIVE}
            label={medicalServiceTranslationKeys.table.errors.loadData}
            displayIndicator
         />
      );
   }

   const hasMorePractitioners = practitioners.length > 1;

   return props.children({
      hasMorePractitioners: hasMorePractitioners,
      onlinePaymentsIntegrationExists: onlinePaymentsIntegrationConfiguration,
   });
};

export const PrivateMedicalServicePanel = (): JSX.Element => {
   return (
      <>
         <Header testId={'privateMedicalService'} margin={MarginPosition.BOTTOM}>
            {entryTranslationKeys.header}
         </Header>
         <PrivateMedicalServicePanelLoader>
            {({ hasMorePractitioners, onlinePaymentsIntegrationExists }) => (
               <PrivateMedicalServiceNavTabs
                  hasMorePractitioners={hasMorePractitioners}
                  onlinePaymentsIntegrationExists={onlinePaymentsIntegrationExists}
               />
            )}
         </PrivateMedicalServicePanelLoader>
      </>
   );
};
